import { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { useAddInvestBox } from './queries';
import { schema } from './schema';
import { ControlledMaskedTextField } from '../../../../../components/FormControlsV2/ControlledMaskedTextField';
import { ControlledSelectField } from '../../../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextField } from '../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../components/FormControlsV2/FormProvider';
import { currencyInfo$ } from '../../../../../effector/currencies';
import { User$ } from '../../../../../effector/user';
import { userInfo } from '../../../../../effector/userInfoList';
import { AggregateInvestbox } from '../../../../../types/aggregateInvestbox';
import { UserType } from '../../../../../types/aggregateUser';

type FormFields = {
  title: string;
  author: string;
  motto: string;
  currency: string;
  minSumma: number;
  tariffId: string;
  tariffName: string;
  leverage: number;
};

export function InvestBoxForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const userStore = useUnit(User$);
  const userInfoStore = useUnit(userInfo);
  const currencyStore = useUnit(currencyInfo$);

  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync } = useAddInvestBox();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues: {
      title: '',
      author: '',
      motto: '',
      currency: '',
      minSumma: 1000,
      tariffId: '',
      tariffName: '',
      leverage: 0,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: FormFields) => {
    setLoading(true);

    const data: Partial<AggregateInvestbox> = {
      title: values.title,
      currency: values.currency,
      managerId: values.author ? Number(values.author) : undefined,
      tariffId: values.tariffId,
      tariffName: values.tariffName,
      leverage: values.leverage,
      subscriptionThreshold: values.minSumma,
      tag: values.motto,
      securities: '[]',
      active: true,
      isOpen: false,
      creatorId: userStore !== undefined && userStore !== null ? userStore.id : 0,
      creationDate: new Date().toDateString(),
    };

    try {
      const result: { errorMessage?: string | null } = await mutateAsync(data);

      if (result.errorMessage) {
        setError(result.errorMessage ?? 'На сервере произошла ошибка');
        return;
      }

      enqueueSnackbar('Инвест-копилка добавлена');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  };

  const userOptions = useMemo(
    () =>
      userInfoStore.total > 0
        ? userInfoStore.users
            .filter((u) => u.userType !== UserType.Representative)
            .map((u) => ({ value: String(u.id), label: u.displayName }))
        : [],
    [userInfoStore.total, userInfoStore.users],
  );

  const currencyOptions = useMemo(
    () => currencyStore.map(({ code, name }) => ({ value: code, label: name })) || [],
    [currencyStore],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <p>Тариф предполагается задавать копированием GIUD-а в соответствующее поле</p>

      <FormProvider control={control} errors={errors}>
        <ControlledTextField name="title" label="Название" />
        <ControlledSelectField name="author" label="Автор" options={userOptions} />

        <ControlledTextField name="subtitle" label="Подзаголовок" />
        <ControlledTextField name="motto" label="Слоган" />

        <ControlledSelectField name="currency" label="Валюта" options={currencyOptions} />
        <ControlledTextField name="minSumma" label="Минимальная сумма" type="number" />

        <ControlledMaskedTextField
          name="tariffId"
          label="Идентификатор тарифа (GUID)"
          mask="********-****-****-****-************"
        />
        <ControlledTextField name="tariffName" label="Название тарифа" />

        <ControlledTextField name="leverage" label="Используемое плечо" type="number" />
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isDirty || loading}
        fullWidth
        sx={{ mt: 2 }}
        startIcon={loading && <CircularProgress size={20} />}
      >
        Добавить
      </Button>
    </form>
  );
}
